import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, Injector, OnDestroy, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { ContextMenuComponent, ContextMenuItem } from '../../context-menu/context-menu.component';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';
import { ContextTreeNodeComponent, NodeMenuRequest } from './components/context-tree-node/context-tree-node.component';
import { OverseerViewMode, OverseerWorkFilter, ProductionOverseerState, ProductionOverseerStore, ReportTarget } from './production-overseer.store';
import { ContextNodeVm, ItemGroupVm, OperationCellVm, OverseerViewModel, StageGroupVm, StageLineVm, StageRowFocus, WorkLineVm } from './models/overseer.view-model';
import { OperationType } from './models/operation-type.model';
import { formatAmount, formatAmountInput, formatNumber } from '../../../utils/format-amount';
import { STRUCTURE_LABELS } from '../../../models/bom-structure';
import { ClickOutsideDirective } from '../../../directives/click-outside.directive';

// The floating report panel (0102) measures ~245 px — 55 px input, gotowe, two buttons, gaps,
// padding — and is shifted left to stay inside the viewport when the node sits near the card's
// right edge
const REPORT_POPOVER_WIDTH = 250;

@Component({
  selector: 'production-overseer',
  templateUrl: './production-overseer.page.html',
  styleUrl: './production-overseer.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgTemplateOutlet, ContextMenuComponent, ItemPhotoComponent, ContextTreeNodeComponent, ClickOutsideDirective]
})
export class ProductionOverseerPage implements OnInit, OnDestroy {
  readonly state$: Observable<ProductionOverseerState>;

  // Only one report form is open at a time, so every row shares one ref
  readonly reportInput = viewChild<ElementRef<HTMLInputElement>>('reportAmount');
  // Same for the header's one inline name input
  readonly headerEditInput = viewChild<ElementRef<HTMLInputElement>>('headerEditInput');
  // The sheet: the focused row is scrolled into view inside it
  readonly workTable = viewChild<ElementRef<HTMLElement>>('workTable');

  formatAmount = formatAmount;
  formatNumber = formatNumber;
  formatAmountInput = formatAmountInput;

  readonly structureLabels = STRUCTURE_LABELS;

  constructor(
    private store: ProductionOverseerStore,
    private destroyRef: DestroyRef,
    private injector: Injector
  ) {
    this.state$ = this.store.state$;

    // The focused row comes into view under the head (0100): a node clicked in DRZEWO may name a
    // row that is off-screen, newly listed or newly expanded, so the scroll waits for the render
    this.store.state$
      .pipe(
        map(state => state.focusedStageRow),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(focus => {
        if (focus) {
          afterNextRender(() => this.scrollFocusedRowIntoView(focus), { injector: this.injector });
        }
      });

    // Focus the freshly opened report form; select the prefill so typing replaces it
    effect(() => {
      const input = this.reportInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });

    // Same for the header input: a rename comes prefilled, select it so typing replaces it
    effect(() => {
      const input = this.headerEditInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  ngOnInit(): void {
    this.store.init().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  setViewMode(viewMode: OverseerViewMode): void {
    this.store.setViewMode(viewMode);
  }

  setWorkFilter(workFilter: OverseerWorkFilter): void {
    this.store.setWorkFilter(workFilter);
  }

  toggleItem(bomItemId: number): void {
    this.store.toggleItem(bomItemId);
  }

  isExpanded(state: ProductionOverseerState, bomItemId: number): boolean {
    return state.expandedItems.includes(bomItemId);
  }

  itemReportTarget(state: ProductionOverseerState, bomItemId: number): ReportTarget | null {
    const target = inRowTarget(state);
    return target?.kind === 'item' && target.bomItemId === bomItemId ? target : null;
  }

  lineReportTarget(state: ProductionOverseerState, lineId: number): ReportTarget | null {
    const target = inRowTarget(state);
    return target?.kind === 'line' && target.lineId === lineId ? target : null;
  }

  isItemCellTarget(state: ProductionOverseerState, bomItemId: number, operationTypeId: number): boolean {
    return this.itemReportTarget(state, bomItemId)?.operationTypeId === operationTypeId;
  }

  isLineCellTarget(state: ProductionOverseerState, lineId: number, operationTypeId: number): boolean {
    return this.lineReportTarget(state, lineId)?.operationTypeId === operationTypeId;
  }

  // A cell is reportable while its row is open and the column still has room
  isCellOpen(row: { isDone: boolean }, cell: OperationCellVm): boolean {
    return !row.isDone && !cell.isDone;
  }

  // A skipped stage reads as a dash; it stays reportable — the report is the newer fact
  cellLabel(cell: OperationCellVm): string {
    return cell.isSkipped ? '—' : (cell.doneAmount ? formatNumber(cell.doneAmount) : '');
  }

  // The trailing Zgłoś (cell = null) is the untagged, completing report — exactly today's
  startItemReport(group: ItemGroupVm, cell: OperationCellVm | null, event: Event): void {
    event.stopPropagation();
    this.store.startReport({
      kind: 'item',
      bomItemId: group.bomItemId,
      operationTypeId: cell?.operationTypeId ?? null,
      prefill: cell ? cell.workableNow : group.workableNowTotal
    });
  }

  startLineReport(line: WorkLineVm, cell: OperationCellVm | null, event: Event): void {
    event.stopPropagation();
    this.store.startReport({
      kind: 'line',
      lineId: line.lineId,
      operationTypeId: cell?.operationTypeId ?? null,
      prefill: cell ? cell.workableNow : line.workableNow
    });
  }

  toggleCompletesItem(): void {
    this.store.toggleCompletesItem();
  }

  confirmReport(rawAmount: string, event: Event): void {
    event.stopPropagation();
    this.store.confirmReport(rawAmount).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  cancelReport(event: Event): void {
    event.stopPropagation();
    this.store.cancelReport();
  }

  undoLastReport(): void {
    this.store.undoLastReport().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- Wg etapów: one stage's queue ---

  selectedStage(state: ProductionOverseerState): OperationType | null {
    return OverseerViewModel.selectedOperationType(state.operationTypes, state.selectedOperationTypeId);
  }

  selectStage(operationTypeId: number): void {
    this.store.selectOperationType(operationTypeId);
  }

  toggleStageGroup(key: string): void {
    this.store.toggleStageGroup(key);
  }

  isStageGroupExpanded(state: ProductionOverseerState, key: string): boolean {
    return state.expandedStageGroups.includes(key);
  }

  // The focus mark sits on the group row for a group focus, and for a line focus while the group
  // is collapsed — a chevron never hides it (0100)
  isStageGroupFocused(state: ProductionOverseerState, key: string): boolean {
    const focus = state.focusedStageRow;
    return focus?.groupKey === key && (focus.lineId == null || !this.isStageGroupExpanded(state, key));
  }

  isStageLineFocused(state: ProductionOverseerState, lineId: number): boolean {
    return state.focusedStageRow?.lineId === lineId;
  }

  stageGroupReportTarget(state: ProductionOverseerState, key: string): ReportTarget | null {
    const target = inRowTarget(state);
    return target?.kind === 'stageGroup' && target.groupKey === key ? target : null;
  }

  // A line row's click does nothing but focus it — the DRZEWO card switches to its zlecenie (0098)
  focusStageLine(group: StageGroupVm, line: StageLineVm): void {
    this.store.focusStageRow(lineFocus(group, line));
  }

  // The tree points back at the list (0100): the clicked node's row comes into view and takes the focus
  focusTreeNode(lineId: number): void {
    this.store.focusLine(lineId);
  }

  // --- the node's menu (0102): the line row's actions, at the selected stage, where the eye is ---

  onTreeContextMenu({ lineId, event }: NodeMenuRequest): void {
    // Right-click at (almost) the same point again: close our menu and let the native one
    // through — the BOM tree node's bypass, as on the strip
    const openAt = this.store.state.treeMenu?.position;
    const samePoint = openAt
      && Math.abs(openAt.x - event.clientX) < 10
      && Math.abs(openAt.y - event.clientY) < 10;

    if (samePoint) {
      this.store.closeTreeMenu();
      return;
    }

    event.preventDefault();

    // right-click focuses, like a click — the menu acts on the node under the cursor, and its
    // row is on screen for the banner and Cofnij the action produces
    this.store.focusLine(lineId);
    this.store.openTreeMenu(lineId, { x: event.clientX, y: event.clientY });
  }

  // The row's three buttons, in the row's words; a node with nothing to act on gets one muted line
  // saying why, so a right-click always answers
  treeMenuItems(state: ProductionOverseerState): ContextMenuItem[] {
    const node = treeMenuNode(state);

    if (!node) {
      return [];
    }

    const items: ContextMenuItem[] = [];

    if (node.canRestore) {
      items.push({ id: 'restore', label: 'Przywróć' });
    }

    if (node.canSkip) {
      items.push({ id: 'skip', label: 'Nic do zrobienia' });
    }

    if (node.canReport) {
      items.push({ id: 'report', label: 'Zgłoś' });
    }

    return items.length > 0
      ? items
      : [{ id: 'none', label: node.isDone ? 'gotowe' : 'po tym etapie', disabled: true }];
  }

  onTreeMenuAction(actionId: string): void {
    const state = this.store.state;
    const menu = state.treeMenu!;
    const node = treeMenuNode(state);
    const stage = this.selectedStage(state);

    if (!node || !stage) {
      return;
    }

    if (actionId === 'report') {
      // the panel opens where the menu was (the BOM tree's Dodaj flow), pulled left to stay on screen
      this.store.startReport({
        kind: 'line',
        lineId: node.lineId,
        operationTypeId: stage.id,
        prefill: node.waiting,
        openAt: { x: Math.min(menu.position.x, window.innerWidth - REPORT_POPOVER_WIDTH - 10), y: menu.position.y }
      });
    }

    if (actionId === 'skip' || actionId === 'restore') {
      this.store
        .skipOperation([node.lineId], stage.id, actionId === 'skip')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  onTreeMenuClose(): void {
    this.store.closeTreeMenu();
  }

  // What the panel's number is for: the node's name and the stage — `Wspornik koła podporowego · Gięcie`
  reportPopoverCaption(state: ProductionOverseerState): string {
    const target = state.reportTarget;
    const line = target?.kind === 'line' ? state.lines.find(line => line.lineId === target.lineId) : undefined;
    const stage = state.operationTypes.find(type => type.id === target?.operationTypeId);

    return [line?.itemName, stage?.name].filter(part => part).join(' · ');
  }

  // Always into the selected stage: what waits is the prefill, the stage's remaining the cap.
  // Every row action focuses the row first, so the card shows what the open form is about
  startStageGroupReport(group: StageGroupVm, event: Event): void {
    event.stopPropagation();
    this.store.focusStageRow(groupFocus(group));
    this.store.startReport({
      kind: 'stageGroup',
      groupKey: group.key,
      operationTypeId: group.operationTypeId,
      prefill: group.waitingTotal
    });
  }

  startStageLineReport(group: StageGroupVm, line: StageLineVm, event: Event): void {
    event.stopPropagation();
    this.store.focusStageRow(lineFocus(group, line));
    this.store.startReport({
      kind: 'line',
      lineId: line.lineId,
      operationTypeId: group.operationTypeId,
      prefill: line.waiting
    });
  }

  // Nic do zrobienia on a row is about how the part is made, not today's pile: every
  // unfinished line of the row skips the stage, arrived or not
  skipStageGroup(group: StageGroupVm, event: Event): void {
    const lines = group.lines.filter(line => !line.isDone && line.state !== 'skipped' && line.doneAtStage === 0);
    this.skipStage(groupFocus(group), lines, group.operationTypeId, true, event);
  }

  skipStageLine(group: StageGroupVm, line: StageLineVm, event: Event): void {
    this.skipStage(lineFocus(group, line), [line], group.operationTypeId, true, event);
  }

  restoreStageGroup(group: StageGroupVm, event: Event): void {
    this.skipStage(groupFocus(group), group.lines.filter(line => line.state === 'skipped'), group.operationTypeId, false, event);
  }

  restoreStageLine(group: StageGroupVm, line: StageLineVm, event: Event): void {
    this.skipStage(lineFocus(group, line), [line], group.operationTypeId, false, event);
  }

  private skipStage(focus: StageRowFocus, lines: StageLineVm[], operationTypeId: number, skips: boolean, event: Event): void {
    event.stopPropagation();
    this.store.focusStageRow(focus);
    this.store
      .skipOperation(lines.map(line => line.lineId), operationTypeId, skips)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  // The row is found by its data attribute, not a viewChild per row. A collapsed group's line is
  // not rendered — its group row carries the focus then, and is the one brought into view
  private scrollFocusedRowIntoView(focus: StageRowFocus): void {
    const table = this.workTable()?.nativeElement;
    const row = (focus.lineId != null ? table?.querySelector(`[data-line-id="${focus.lineId}"]`) : null)
      ?? table?.querySelector(`[data-row-key="${focus.groupKey}"]`);
    const head = table?.querySelector('.table-head');

    if (!table || !row || !head) {
      return;
    }

    row.scrollIntoView({ block: 'nearest' });

    // 'nearest' knows nothing about the sticky head: a row that landed under it is moved below
    const hidden = head.getBoundingClientRect().bottom - row.getBoundingClientRect().top;

    if (hidden > 0) {
      table.scrollTop -= hidden;
    }
  }

  // --- header: the dictionary's one UI ---

  isInsertAt(state: ProductionOverseerState, position: number): boolean {
    return state.headerEdit?.kind === 'insert' && state.headerEdit.position === position;
  }

  isRenaming(state: ProductionOverseerState, operationTypeId: number): boolean {
    return state.headerEdit?.kind === 'rename' && state.headerEdit.id === operationTypeId;
  }

  // A frozen column's menu omits rename and delete; the hover explains why
  headerTitle(operationType: OperationType): string {
    return operationType.inUse
      ? `${operationType.name} · w użyciu — nazwy nie można zmienić ani usunąć`
      : operationType.name;
  }

  headerMenuItems(state: ProductionOverseerState): ContextMenuItem[] {
    const operationType = state.operationTypes.find(type => type.id === state.headerMenu?.operationTypeId);

    const items: ContextMenuItem[] = [
      { id: 'insert-before', label: 'Wstaw przed' },
      { id: 'insert-after', label: 'Wstaw po' }
    ];

    if (operationType && !operationType.inUse) {
      items.push({ id: 'rename', label: 'Zmień nazwę' }, { id: 'delete', label: 'Usuń' });
    }

    return items;
  }

  onHeaderContextMenu(event: MouseEvent, operationType: OperationType): void {
    // Right-click at (almost) the same point again: close our menu and let the native one
    // through — same bypass as the BOM tree node, for the same reason
    const openAt = this.store.state.headerMenu?.position;
    const samePoint = openAt
      && Math.abs(openAt.x - event.clientX) < 10
      && Math.abs(openAt.y - event.clientY) < 10;

    if (samePoint) {
      this.store.closeHeaderMenu();
      return;
    }

    event.preventDefault();

    this.store.openHeaderMenu(operationType.id, { x: event.clientX, y: event.clientY });
  }

  onHeaderMenuAction(actionId: string): void {
    const menu = this.store.state.headerMenu!;
    const operationType = this.store.state.operationTypes.find(type => type.id === menu.operationTypeId);

    if (!operationType) {
      return;
    }

    if (actionId === 'insert-before') {
      this.store.startHeaderEdit({ kind: 'insert', position: operationType.position });
    }

    if (actionId === 'insert-after') {
      this.store.startHeaderEdit({ kind: 'insert', position: operationType.position + 1 });
    }

    if (actionId === 'rename') {
      this.store.startHeaderEdit({ kind: 'rename', id: operationType.id });
    }

    if (actionId === 'delete') {
      this.store.deleteOperation(operationType.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  onHeaderMenuClose(): void {
    this.store.closeHeaderMenu();
  }

  startAddOperation(state: ProductionOverseerState): void {
    this.store.startHeaderEdit({ kind: 'insert', position: state.operationTypes.length });
  }

  submitHeaderEdit(name: string, event: Event): void {
    event.stopPropagation();
    this.store.submitHeaderEdit(name).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  cancelHeaderEdit(event: Event): void {
    event.stopPropagation();
    this.store.cancelHeaderEdit();
  }

  stop(event: Event): void {
    event.stopPropagation();
  }
}

function groupFocus(group: StageGroupVm): StageRowFocus {
  return { groupKey: group.key, lineId: null };
}

// The rows render the form only when it sits in a row; the floating panel (0102) leaves the
// focused row its buttons
function inRowTarget(state: ProductionOverseerState): ReportTarget | null {
  const target = state.reportTarget;
  return target && target.openAt === null ? target : null;
}

function treeMenuNode(state: ProductionOverseerState): ContextNodeVm | null {
  const lineId = state.treeMenu?.lineId;
  return lineId != null && state.contextTree ? findNode(state.contextTree.root, lineId) : null;
}

function findNode(node: ContextNodeVm, lineId: number): ContextNodeVm | null {
  if (node.lineId === lineId) {
    return node;
  }

  for (const child of node.children) {
    const found = findNode(child, lineId);

    if (found) {
      return found;
    }
  }

  return null;
}

function lineFocus(group: StageGroupVm, line: StageLineVm): StageRowFocus {
  return { groupKey: group.key, lineId: line.lineId };
}
